import { useQuery } from '@tanstack/react-query';

import { getExecutionTranscript } from '@/lib/api/generated/api';
import type { TranscriptEntry } from '../transcript';

import { LIVE_POLL_MS } from '../liveCadence';
import { taskKeys } from '../queryKeys';

/**
 * One run's transcript and prompt, fetched when its detail is opened (spec 089 US3).
 *
 * These used to arrive on every task in the workspace aggregate, which the board polls
 * every two seconds. A transcript is uncapped and only this surface renders one, so
 * spec 081 took it off that read — and this is where it comes back, for the one run
 * someone actually expanded.
 *
 * Same hand-written-`queryFn` convention as `useTaskMoves` and `useTaskComments`: the
 * generated raw fetcher, not the generated query options.
 *
 * `enabled` is the whole performance story. A collapsed run issues no request at all,
 * and the cache answers the reopen — closing and expanding a finished run refetches
 * nothing, with no hand-rolled "already fetched" bookkeeping to get wrong.
 */
export function useExecutionTranscript(
  workspaceId: string,
  taskId: string,
  executionId: string,
  { enabled, isFinished }: { enabled: boolean; isFinished: boolean },
) {
  const query = useQuery({
    queryKey: taskKeys.transcript(workspaceId, taskId, executionId),
    queryFn: async (): Promise<{
      transcript: TranscriptEntry[] | null;
      promptSnapshot: string | null;
    }> => {
      const response = await getExecutionTranscript(
        workspaceId,
        taskId,
        executionId,
      );
      if (response.status !== 200) {
        throw new Error('The transcript could not be loaded.');
      }
      return {
        // `null` and `[]` both survive the trip: "never recovered" and "recorded and
        // empty" are different answers, and collapsing them would let a lost
        // transcript read as a run that did nothing (FR-010).
        transcript: response.data.transcript ?? null,
        promptSnapshot: response.data.prompt_snapshot ?? null,
      };
    },
    enabled,
    // A finished run's transcript is written once, when the run ends (064), and never
    // changes again — so re-expanding it must not refetch. Without this the query is
    // stale the moment it resolves, and every reopen goes back to the network for a
    // body that cannot have changed.
    //
    // A run still in flight is the opposite case: it has no transcript yet, so
    // reopening it *should* look again rather than cache an absence forever. That is
    // why this is the run's own state and not a duration someone guessed.
    staleTime: isFinished ? Infinity : 0,
    // The run so far, while it runs (spec 097 FR-001). The service stores a
    // snapshot about every ten seconds; re-reading on the board's own cadence
    // keeps the drawer within one tick of it and costs nothing while the run
    // is collapsed (`enabled` is false) or finished (`isFinished` is true).
    refetchInterval: enabled && !isFinished ? LIVE_POLL_MS : false,
    retry: false,
  });

  return {
    transcript: query.data?.transcript,
    promptSnapshot: query.data?.promptSnapshot,
    // `isPending` is true before an unenabled query has ever run, so the caller pairs
    // it with `enabled` rather than treating it as "in flight" on its own.
    isPending: query.isPending,
    loadFailed: query.isError,
    retry: () => void query.refetch(),
    isFetching: query.isFetching,
  };
}
